import { fetchReapproNeedsByStoreAndDate } from '@gozoki/api';
import type { Product, Store } from '@gozoki/api-types';
import { DateTime } from 'luxon';
import type { ProductWithCount } from '../../pages/dashboard/DashboardPage';

// =============================================================================
// TYPES
// =============================================================================

export interface ProductWithCountAndNeeded extends ProductWithCount {
    needed: number;
}

export interface ReceptionTotals {
    totalDelivered: number;
    totalNeeded: number;
    totalDifference: number;
    totalSurplus: number;
    totalMissing: number;
    serviceRate: number; // Taux de service: min(delivered, needed) / needed for products with needed >= 1
}

export interface CategoryGroup {
    name: string;
    products: ProductWithCountAndNeeded[];
}

// =============================================================================
// SHARED CALCULATION FUNCTIONS
// =============================================================================

/**
 * Gets the category name for a product, with fallback to 'Sans catégorie'
 */
export const getCategoryName = (product: ProductWithCountAndNeeded): string => {
    return product.internalCategory.name;
};

/**
 * Groups products by category while preserving the order of appearance.
 * Returns an array of category groups with their products.
 */
export const groupProductsByCategory = (products: ProductWithCountAndNeeded[]): CategoryGroup[] => {
    const categoryMap = new Map<string, ProductWithCountAndNeeded[]>();
    const categoryOrder: string[] = [];

    products.forEach((product) => {
        const categoryName = getCategoryName(product);
        if (!categoryMap.has(categoryName)) {
            categoryMap.set(categoryName, []);
            categoryOrder.push(categoryName);
        }
        categoryMap.get(categoryName)!.push(product);
    });

    return categoryOrder.map((name) => ({
        name,
        products: categoryMap.get(name)!,
    }));
};

/**
 * Calculates totals for a list of products.
 * - totalDifference is the sum of individual absolute differences (not abs of total diff)
 * - totalSurplus is the sum of positive differences (delivered > needed)
 * - totalMissing is the sum of negative differences (delivered < needed)
 * - serviceRate is the ratio of fulfilled demand: sum(min(delivered, needed)) / sum(needed) for needed >= 1
 */
export const calculateTotals = (products: ProductWithCountAndNeeded[]): ReceptionTotals => {
    const result = products.reduce(
        (acc, p) => {
            const diff = p.count - p.needed;
            const absDiff = Math.abs(diff);

            return {
                totalDelivered: acc.totalDelivered + p.count,
                totalNeeded: acc.totalNeeded + p.needed,
                totalDifference: acc.totalDifference + absDiff,
                totalSurplus: acc.totalSurplus + (diff > 0 ? diff : 0),
                totalMissing: acc.totalMissing + (diff < 0 ? absDiff : 0),
            };
        },
        {
            totalDelivered: 0,
            totalNeeded: 0,
            totalDifference: 0,
            totalSurplus: 0,
            totalMissing: 0,
        }
    );

    return {
        totalDelivered: result.totalDelivered,
        totalNeeded: result.totalNeeded,
        totalDifference: result.totalDifference,
        totalSurplus: result.totalSurplus,
        totalMissing: result.totalMissing,
        serviceRate: result.totalNeeded > 0 ? 1 - result.totalDifference / result.totalNeeded : 0,
    };
};

/**
 * Calculates the signed difference between delivered and needed for a single product.
 * Positive when delivered >= needed, negative otherwise.
 */
export const calculateProductDifference = (product: ProductWithCountAndNeeded): number => {
    return product.count - product.needed;
};

// =============================================================================
// DATA PREPARATION FUNCTIONS
// =============================================================================

/**
 * Aggregates reappro needs across multiple dates by summing counts for each product.
 * This is useful when generating reception files for a time period with multiple inventories.
 */
async function aggregateReapproNeedsAcrossDates(
    store: Store,
    dates: DateTime[]
): Promise<Record<string, { count: number; product: Product }>> {
    if (!store.checkpointStoreRef || dates.length === 0) {
        return {};
    }

    // Fetch reappro needs for each date
    const allNeedsPromises = dates.map((date) => {
        const needsDate = date.minus({ days: 1 }).toISODate();
        if (!needsDate) return Promise.resolve({});
        return fetchReapproNeedsByStoreAndDate(store.checkpointStoreRef!, needsDate);
    });

    const allNeedsResults = await Promise.all(allNeedsPromises);

    // Aggregate needs by product reference, summing counts
    const aggregatedNeeds: Record<string, { count: number; product: Product }> = {};

    allNeedsResults.forEach((needsForDate) => {
        Object.entries(needsForDate).forEach(([productReference, reapproData]) => {
            const typedReapproData = reapproData as { count: number; product: Product };
            if (aggregatedNeeds[productReference]) {
                // Sum the counts
                aggregatedNeeds[productReference].count += typedReapproData.count;
            } else {
                // First occurrence, store the product data
                aggregatedNeeds[productReference] = {
                    count: typedReapproData.count,
                    product: typedReapproData.product,
                };
            }
        });
    });

    return aggregatedNeeds;
}

/**
 * Prepares reception data by merging delivered products with reappro needs.
 * This utility centralizes the logic for fetching and processing reception data
 * to avoid duplication across multiple components.
 *
 * @param deliveredProducts - Already aggregated products with counts
 * @param store - The store to fetch reappro needs for
 * @param receptionDate - The reception date (needs are fetched for the day before)
 */
export async function prepareReceptionData(
    deliveredProducts: ProductWithCount[],
    store: Store,
    receptionDate: DateTime
): Promise<ProductWithCountAndNeeded[]> {
    const needsDate = receptionDate.minus({ days: 1 }).toISODate();

    // Fetch reappro needs from the database with product details
    let reapproNeedsWithProducts: Record<string, { count: number; product: Product }> = {};
    if (store.checkpointStoreRef && needsDate) {
        reapproNeedsWithProducts = await fetchReapproNeedsByStoreAndDate(
            store.checkpointStoreRef,
            needsDate
        );
    }

    // Merge products with their needed counts
    const deliveredProductsMap = new Map<string, ProductWithCountAndNeeded>();

    // First, add all delivered products with their needed counts
    deliveredProducts.forEach((product) => {
        const reapproData = reapproNeedsWithProducts[product.reference];
        deliveredProductsMap.set(product.reference, {
            ...product,
            needed: reapproData?.count ?? 0,
        });
    });

    // Then, add products that were requested but not delivered (count = 0)
    Object.entries(reapproNeedsWithProducts).forEach(([productReference, reapproData]) => {
        if (!deliveredProductsMap.has(productReference)) {
            const { product } = reapproData;

            deliveredProductsMap.set(productReference, {
                ...product,
                count: 0,
                needed: reapproData.count,
                ca: 0,
            });
        }
    });

    return Array.from(deliveredProductsMap.values());
}

/**
 * Prepares reception data for multiple inventories across a time period.
 * Aggregates reappro needs from all inventory dates and merges with delivered products.
 *
 * @param deliveredProducts - Already aggregated products with counts
 * @param store - The store to fetch reappro needs for
 * @param inventoryDates - The dates of inventories (needs are fetched for the day before each)
 */
export async function prepareReceptionDataForPeriod(
    deliveredProducts: ProductWithCount[],
    store: Store,
    inventoryDates: DateTime[]
): Promise<ProductWithCountAndNeeded[]> {
    // Fetch and aggregate reappro needs across all inventory dates
    const reapproNeedsWithProducts = await aggregateReapproNeedsAcrossDates(store, inventoryDates);

    // Merge products with their needed counts
    const deliveredProductsMap = new Map<string, ProductWithCountAndNeeded>();

    // First, add all delivered products with their needed counts
    deliveredProducts.forEach((product) => {
        const reapproData = reapproNeedsWithProducts[product.reference];
        deliveredProductsMap.set(product.reference, {
            ...product,
            needed: reapproData?.count ?? 0,
        });
    });

    // Then, add products that were requested but not delivered (count = 0)
    Object.entries(reapproNeedsWithProducts).forEach(([productReference, reapproData]) => {
        if (!deliveredProductsMap.has(productReference)) {
            const { product } = reapproData;
            deliveredProductsMap.set(productReference, {
                ...product,
                count: 0,
                needed: reapproData.count,
                ca: 0,
            });
        }
    });

    return Array.from(deliveredProductsMap.values());
}
